import { useCallback, useEffect, useMemo, useState } from 'react';
import type { ApiAdminUser } from '@/lib/types';
import { getAdminUsers, banUser, unbanUser, changeUserRole } from '@/services/adminService';
import { userRoleMeta, ROLE_OPTIONS } from '../adminMeta';

export default function UsersTab() {
  const [users, setUsers] = useState<ApiAdminUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState<'all' | 'banned' | 'admin' | 'moderator'>('all');
  const [banTarget, setBanTarget] = useState<ApiAdminUser | null>(null);
  const [banReason, setBanReason] = useState('');
  const [banUntil, setBanUntil] = useState('');
  const [pendingId, setPendingId] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await getAdminUsers();
      setUsers(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erreur de chargement');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return users.filter((u) => {
      const name = (u.displayName ?? u.name ?? u.email ?? '').toLowerCase();
      const email = (u.email ?? '').toLowerCase();
      const meta = userRoleMeta(u.roles);
      if (q && !name.includes(q) && !email.includes(q)) return false;
      if (roleFilter === 'banned' && !u.isBanned) return false;
      if (roleFilter === 'admin' && !meta.isAdmin) return false;
      if (roleFilter === 'moderator' && !meta.isModerator) return false;
      return true;
    });
  }, [users, search, roleFilter]);

  const handleBan = async () => {
    if (!banTarget || !banReason.trim()) return;
    setPendingId(banTarget.id);
    try {
      await banUser(banTarget.id, {
        reason: banReason.trim(),
        bannedUntil: banUntil ? new Date(banUntil).toISOString() : null,
      });
      setUsers((prev) =>
        prev.map((u) =>
          u.id === banTarget.id ? { ...u, isBanned: true, banReason: banReason.trim(), bannedUntil: banUntil || null } : u
        )
      );
      setBanTarget(null);
      setBanReason('');
      setBanUntil('');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Impossible de bannir');
    } finally {
      setPendingId(null);
    }
  };

  const handleUnban = async (u: ApiAdminUser) => {
    setPendingId(u.id);
    try {
      await unbanUser(u.id);
      setUsers((prev) => prev.map((x) => (x.id === u.id ? { ...x, isBanned: false, banReason: null, bannedUntil: null } : x)));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Impossible de débannir');
    } finally {
      setPendingId(null);
    }
  };

  const handleRole = async (u: ApiAdminUser, role: string) => {
    setPendingId(u.id);
    try {
      await changeUserRole(u.id, { role });
      setUsers((prev) => prev.map((x) => (x.id === u.id ? { ...x, roles: [role] } : x)));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Impossible de changer le rôle');
    } finally {
      setPendingId(null);
    }
  };

  const bannedCount = users.filter((u) => u.isBanned).length;

  return (
    <div>
      {/* Search + filters */}
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400 text-sm"></i>
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Rechercher par nom ou email…"
            className="w-full pl-9 pr-3 py-2 rounded-md border border-background-200 bg-white text-sm text-foreground-950 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-300"
          />
        </div>
        <div className="inline-flex rounded-full bg-background-100 p-1 self-start">
          {[
            { value: 'all', label: 'Tous' },
            { value: 'admin', label: 'Admins' },
            { value: 'moderator', label: 'Modos' },
            { value: 'banned', label: `Bannis (${bannedCount})` },
          ].map((f) => (
            <button
              key={f.value}
              type="button"
              onClick={() => setRoleFilter(f.value as typeof roleFilter)}
              className={`px-4 py-1.5 rounded-full text-xs font-semibold transition-colors cursor-pointer whitespace-nowrap ${roleFilter === f.value
                ? 'bg-white text-foreground-950'
                : 'text-foreground-500 hover:text-foreground-700'
                }`}
            >
              {f.label}
            </button>
          ))}
        </div>
      </div>

      {/* List */}
      <div className="mt-4 flex flex-col gap-3">
        {loading ? (
          [1, 2, 3].map((i) => (
            <div key={i} className="bg-white border border-background-200 rounded-lg p-5 animate-pulse">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-background-100"></div>
                <div className="flex-1">
                  <div className="h-3 bg-background-100 rounded w-32 mb-2"></div>
                  <div className="h-3 bg-background-100 rounded w-48"></div>
                </div>
              </div>
            </div>
          ))
        ) : error ? (
          <div className="bg-white border border-background-200 rounded-lg p-8 text-center">
            <p className="text-sm text-foreground-500">{error}</p>
            <button
              type="button"
              onClick={load}
              className="mt-3 px-4 py-2 rounded-md bg-primary-500 text-white text-sm font-semibold hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
            >
              Réessayer
            </button>
          </div>
        ) : filtered.length === 0 ? (
          <div className="bg-white border border-background-200 rounded-lg p-10 text-center">
            <div className="w-14 h-14 mx-auto rounded-full bg-background-100 flex items-center justify-center text-foreground-400">
              <i className="ri-user-search-line text-2xl"></i>
            </div>
            <h3 className="mt-4 font-heading text-lg font-semibold text-foreground-950">
              Aucun utilisateur
            </h3>
            <p className="mt-1 text-sm text-foreground-500">Aucun résultat pour cette recherche.</p>
          </div>
        ) : (
          filtered.map((u) => (
            <UserCard
              key={u.id}
              user={u}
              pending={pendingId === u.id}
              onBan={() => setBanTarget(u)}
              onUnban={() => handleUnban(u)}
              onRole={(role) => handleRole(u, role)}
            />
          ))
        )}
      </div>

      {/* Ban modal */}
      {banTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center px-4">
          <div className="absolute inset-0 bg-foreground-950/40" onClick={() => setBanTarget(null)} aria-hidden="true"></div>
          <div className="relative w-full max-w-md bg-white border border-background-200 rounded-lg p-6 animate-scale-in">
            <h3 className="font-heading text-lg font-semibold text-foreground-950">
              Bannir {banTarget.displayName ?? banTarget.name ?? banTarget.email}
            </h3>
            <p className="mt-1 text-sm text-foreground-500">
              Cette personne ne pourra plus se connecter tant que le bannissement est actif.
            </p>

            <label className="block mt-4 text-sm font-medium text-foreground-700">
              Motif du bannissement
            </label>
            <textarea
              value={banReason}
              onChange={(e) => setBanReason(e.target.value)}
              maxLength={500}
              rows={3}
              placeholder="Explique la raison…"
              className="mt-1 w-full px-3 py-2 rounded-md border border-background-200 bg-white text-sm text-foreground-950 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-300 resize-none"
            />

            <label className="block mt-3 text-sm font-medium text-foreground-700">
              Jusqu'à (optionnel)
            </label>
            <input
              type="datetime-local"
              value={banUntil}
              onChange={(e) => setBanUntil(e.target.value)}
              className="mt-1 w-full px-3 py-2 rounded-md border border-background-200 bg-white text-sm text-foreground-950 focus:outline-none focus:ring-2 focus:ring-primary-300"
            />

            <div className="mt-6 flex justify-end gap-3">
              <button
                type="button"
                onClick={() => setBanTarget(null)}
                className="px-4 py-2 rounded-md text-sm font-semibold text-foreground-600 hover:bg-background-100 transition-colors cursor-pointer whitespace-nowrap"
              >
                Annuler
              </button>
              <button
                type="button"
                onClick={handleBan}
                disabled={!banReason.trim() || pendingId === banTarget.id}
                className="px-4 py-2 rounded-md bg-accent-500 text-white text-sm font-semibold hover:bg-accent-600 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {pendingId === banTarget.id ? 'Traitement…' : 'Bannir'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function UserCard({
  user,
  pending,
  onBan,
  onUnban,
  onRole,
}: {
  user: ApiAdminUser;
  pending: boolean;
  onBan: () => void;
  onUnban: () => void;
  onRole: (role: string) => void;
}) {
  const meta = userRoleMeta(user.roles);
  const name = user.displayName ?? user.name ?? user.email ?? 'Utilisateur';
  const initials = name
    .split(' ')
    .map((p) => p[0])
    .slice(0, 2)
    .join('')
    .toUpperCase();
  const currentRoleValue = meta.isAdmin ? 'Admin' : meta.isModerator ? 'Moderator' : 'Member';

  return (
    <div className="bg-white border border-background-200 rounded-lg p-5">
      <div className="flex flex-wrap items-center gap-4">
        <span className="w-10 h-10 rounded-full bg-primary-500 text-white flex items-center justify-center text-sm font-bold shrink-0">
          {initials}
        </span>

        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <p className="text-sm font-semibold text-foreground-950 truncate">{name}</p>
            {meta.isAdmin && (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-primary-100 text-primary-900 text-[11px] font-semibold">
                <i className="ri-vip-crown-line text-xs"></i>
                {meta.label}
              </span>
            )}
            {meta.isModerator && (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-secondary-100 text-secondary-900 text-[11px] font-semibold">
                <i className="ri-shield-star-line text-xs"></i>
                {meta.label}
              </span>
            )}
            {user.isBanned && (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-accent-100 text-accent-900 text-[11px] font-semibold">
                <i className="ri-forbid-2-line text-xs"></i>
                Banni
              </span>
            )}
          </div>
          <p className="text-xs text-foreground-400 truncate mt-0.5">{user.email}</p>
          {user.isBanned && user.banReason && (
            <p className="text-xs text-foreground-500 mt-1 truncate">
              Motif : {user.banReason}
            </p>
          )}
        </div>

        <div className="flex items-center gap-2 flex-wrap">
          <div className="relative">
            <select
              value={currentRoleValue}
              disabled={pending}
              onChange={(e) => onRole(e.target.value)}
              className="appearance-none pl-3 pr-8 py-2 rounded-md border border-background-200 bg-white text-sm font-medium text-foreground-700 focus:outline-none focus:ring-2 focus:ring-primary-300 cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {ROLE_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
            <i className="ri-arrow-down-s-line absolute right-2.5 top-1/2 -translate-y-1/2 text-foreground-400 pointer-events-none text-sm"></i>
          </div>

          {user.isBanned ? (
            <button
              type="button"
              disabled={pending}
              onClick={onUnban}
              className="px-3 py-2 rounded-md text-sm font-semibold text-secondary-900 bg-secondary-100 hover:bg-secondary-200 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-60 disabled:cursor-not-allowed"
            >
              Débannir
            </button>
          ) : (
            <button
              type="button"
              disabled={pending}
              onClick={onBan}
              className="px-3 py-2 rounded-md text-sm font-semibold text-accent-900 bg-accent-100 hover:bg-accent-200 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-60 disabled:cursor-not-allowed"
            >
              Bannir
            </button>
          )}
        </div>
      </div>
    </div>
  );
}